import * as React from 'react';

import { Activity, Topic } from './interfaces';
import { AVERAGE_FONT_WIDTH, TOPIC_FILTERS } from './shared';

import { Tooltip, Checkbox, DISABLED, INDETERMINATE, } from '../../../../../Shared/index';

const dropdownIconSrc = `${window.postbuildEnv.CDN_URL}/images/icons/dropdown.svg`

const LEVEL_THREE = 3
const LEVEL_TWO = 2

interface Grouping {
  levelTwoIds: string[],
  levelOneIds: string[]
  group: string
}

interface IndividualTopicFilterRowProps {
  topicFilters: number[],
  topicKey: number,
  handleTopicFilterChange: (topicFilters: number[]) => void,
  uniqueLevelTwoTopics: Topic[],
  uniqueLevelOneTopics: Topic[],
  filteredActivities: Activity[],
}

interface TopicToggleProps {
  filteredActivities: Activity[],
  grouping: Grouping,
  uniqueLevelTwoTopics: Topic[],
  uniqueLevelOneTopics: Topic[],
  topicFilters: number[],
  handleTopicFilterChange: (topicFilters: number[]) => void,
  level: number,
  topics: Topic[]
}

interface TopicFiltersProps {
  activities: Activity[],
  filterActivities: (ignoredKey?: string) => Activity[]
  topicFilters: number[],
  handleTopicFilterChange: (topicFilters: number[]) => void,
  topics: Topic[],
}

const IndividualTopicFilterRow = ({ topicFilters, topicKey, handleTopicFilterChange, uniqueLevelOneTopics, filteredActivities, }: IndividualTopicFilterRowProps) => {
  const activityCount = filteredActivities.filter(act => act.topics && act.topics.find(t => t.id === topicKey)).length
  const selected = topicFilters.includes(topicKey)
  const disabled = activityCount === 0

  function toggleCheckbox() {
    if (selected) {
      uncheckIndividualFilter()
    } else {
      checkIndividualFilter()
    }
  }

  function checkIndividualFilter() {
    const newTopicFilters = Array.from(new Set(topicFilters.concat([topicKey])))
    handleTopicFilterChange(newTopicFilters)
  }

  function uncheckIndividualFilter() {
    const newTopicFilters = topicFilters.filter(k => k !== topicKey)
    handleTopicFilterChange(newTopicFilters)
  }

  const topic = uniqueLevelOneTopics.find(t => t.id === topicKey)

  const topicNameElement = topic.name.length * AVERAGE_FONT_WIDTH >= 182 ? <Tooltip tooltipText={topic.name} tooltipTriggerId={topic.name} tooltipTriggerText={topic.name} tooltipTriggerTextClass="tooltip-trigger-text" /> : <span id={topic.name}>{topic.name}</span>

  return (
    <div className="level-one-row filter-row topic-row" key={topicKey}>
      <div className="checkbox-row">
        <Checkbox
          ariaLabel={`Filter by ${topic.name}`}
          onClick={toggleCheckbox}
          selected={selected}
          state={disabled ? DISABLED : null}
        />
        {topicNameElement}
      </div>
      <span>({activityCount})</span>
    </div>
  )
}

const TopicToggle = ({ filteredActivities, grouping, uniqueLevelTwoTopics, uniqueLevelOneTopics, topicFilters, handleTopicFilterChange, level, topics, }: TopicToggleProps) => {
  const [isOpen, setIsOpen] = React.useState(false)

  function toggleIsOpen() { setIsOpen(!isOpen) }

  const selected = grouping.levelOneIds.some(levelOneId => topicFilters.includes(levelOneId))
  const topLevelActivityCount = filteredActivities.filter(act => act.topics && act.topics.some(t => grouping.levelOneIds.includes(t.id))).length
  const disabled = topLevelActivityCount === 0
  const indeterminate = selected && !grouping.levelOneIds.every(levelOneId => topicFilters.includes(levelOneId))

  let checkboxState

  if (indeterminate) {
    checkboxState = INDETERMINATE
  } else if (disabled) {
    checkboxState = DISABLED
  }

  function toggleTopLevelCheckbox() {
    if (selected) {
      uncheckAllFilters()
    } else {
      checkAllFilters()
    }
  }

  function uncheckAllFilters() {
    const newTopicFilters = topicFilters.filter(levelOneId => !grouping.levelOneIds.includes(levelOneId))
    handleTopicFilterChange(newTopicFilters)
  }

  function checkAllFilters() {
    const newTopicFilters = Array.from(new Set(topicFilters.concat(grouping.levelOneIds)))
    handleTopicFilterChange(newTopicFilters)
  }

  const toggleArrow = <button aria-describedby={grouping.group} aria-expanded={isOpen} aria-label={`${isOpen ? 'Hide' : 'Show'} nested topics`} className="interactive-wrapper focus-on-light filter-toggle-button" onClick={toggleIsOpen} type="button"><img alt="" className={isOpen ? 'is-open' : 'is-closed'} src={dropdownIconSrc} /></button>

  const topLevelCheckbox = (
    <Checkbox
      ariaLabel={`Filter by ${grouping.group}`}
      onClick={toggleTopLevelCheckbox}
      selected={selected}
      state={checkboxState}
    />
  )

  let individualFilters = <span />

  if (isOpen) {
    if (level === LEVEL_THREE) {
      individualFilters = grouping.levelTwoIds.map((levelTwoId: string) => {
        const levelTwoTopic = topics.find(t => t.id === levelTwoId);
        const filteredLevelOneTopics = uniqueLevelOneTopics.filter(t => t.parent_id === levelTwoTopic.id)
        const filteredGrouping = { group: levelTwoTopic.name, levelTwoIds: grouping.levelTwoIds, levelOneIds: filteredLevelOneTopics.map(t => t.id) }
        return (
          <TopicToggle
            filteredActivities={filteredActivities}
            grouping={filteredGrouping}
            handleTopicFilterChange={handleTopicFilterChange}
            key={levelTwoTopic.name}
            level={LEVEL_TWO}
            topicFilters={topicFilters}
            uniqueLevelOneTopics={uniqueLevelOneTopics}
            uniqueLevelTwoTopics={uniqueLevelTwoTopics}
          />
        )
      }
      )
    } else {
      individualFilters = grouping.levelOneIds.map((levelOneId: string) =>
        (<IndividualTopicFilterRow
          filteredActivities={filteredActivities}
          handleTopicFilterChange={handleTopicFilterChange}
          key={levelOneId}
          topicFilters={topicFilters}
          topicKey={levelOneId}
          uniqueLevelOneTopics={uniqueLevelOneTopics}
          uniqueLevelTwoTopics={uniqueLevelTwoTopics}
        />)
      )
    }
  }

  const className = level === LEVEL_THREE ? "top-level" : "individual-row"

  return (
    <section className="toggle-section activity-classification-toggle">
      <div className={className + " filter-row"}>
        <div>
          {toggleArrow}
          {topLevelCheckbox}
          <span id={grouping.group}>{grouping.group}</span>
        </div>
        <span>({topLevelActivityCount})</span>
      </div>
      {individualFilters}
    </section>
  )
}

const TopicFilters = ({ activities, filterActivities, topicFilters, handleTopicFilterChange, topics, }: TopicFiltersProps) => {
  let levelOneTopicsInUse = []
  let uniqueLevelOneTopics = []
  let uniqueLevelTwoTopics = []
  let topicGroupings = []
  const filteredActivities = filterActivities(TOPIC_FILTERS)

  function clearAllTopicFilters() { handleTopicFilterChange([]) }

  function getUniqueTopics(topics) {
    const nonNullTopics = topics.filter(t => t?.id)
    const topicIdsArray = Array.from(new Set(nonNullTopics.map(a => a.id)))
    return topicIdsArray.map(id => nonNullTopics.find(t => t.id === id))
  }

  if (topics.length) {
    activities.forEach(a => { levelOneTopicsInUse = levelOneTopicsInUse.concat(a.topics) })
    uniqueLevelOneTopics = getUniqueTopics(levelOneTopicsInUse)

    const levelTwoTopicsInUse = uniqueLevelOneTopics.map(levelOneTopic => topics.find(topic => topic.id === levelOneTopic.parent_id))
    uniqueLevelTwoTopics = getUniqueTopics(levelTwoTopicsInUse)

    const levelThreeTopicsInUse = uniqueLevelTwoTopics.map(levelTwoTopic => topics.find(topic => topic.id === levelTwoTopic.parent_id))
    const uniqueLevelThreeTopics = getUniqueTopics(levelThreeTopicsInUse)

    topicGroupings = uniqueLevelThreeTopics.sort((a, b) => a.name.localeCompare(b.name)).map(levelThree => {
      const levelTwoIds = uniqueLevelTwoTopics.filter(levelTwo => levelTwo.parent_id === levelThree.id).sort((a, b) => a.name.localeCompare(b.name)).map(levelTwo => levelTwo.id)
      return {
        group: levelThree.name,
        levelTwoIds: levelTwoIds,
        levelOneIds: uniqueLevelOneTopics.filter(levelOne => levelTwoIds.includes(levelOne.parent_id)).sort((a, b) => a.name.localeCompare(b.name)).map(levelOne => levelOne.id)
      }
    })
  }

  const topicToggles = topicGroupings.map(grouping =>
    (<TopicToggle
      filteredActivities={filteredActivities}
      grouping={grouping}
      handleTopicFilterChange={handleTopicFilterChange}
      key={grouping.group}
      level={LEVEL_THREE}
      topicFilters={topicFilters}
      topics={topics}
      uniqueLevelOneTopics={uniqueLevelOneTopics}
      uniqueLevelTwoTopics={uniqueLevelTwoTopics}
    />)
  )

  const clearButton = topicFilters.length ? <button className="interactive-wrapper clear-filter focus-on-light" onClick={clearAllTopicFilters} type="button">Clear</button> : <span />
  return (
    <section aria-labelledby="topicsTitle" className="filter-section">
      <div className="name-and-clear-wrapper">
        <h2 id="topicsTitle">Topics</h2>
        {clearButton}
      </div>
      {topicToggles}
    </section>
  )
}

export default TopicFilters
